import * as React from "react";
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "components/ui/table";
import { DataTablePagination } from "./data-table-pagination";
import { DataTableViewOptions } from "./data-table-view-options";
import { Input } from "../input";
import { ExportExcel } from "../ExportExcel";
import { Skeleton } from "./data-table-skeleton";
import { Button } from "../ButtonGs";
import { Pagination } from "interfaces/pagination";

interface ISelectionAction {
  label: string;
  action: (data: any) => void;
  hasPermission: boolean;
  variant?:
    | "primary"
    | "secondary"
    | "tertiary"
    | "danger"
    | "quartiary"
    | undefined;
}

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  filterBy?: string;
  exportTable?: boolean;
  exportTableData?: any[];
  isLoading?: boolean;
  showControlColumns?: boolean;
  pageSize?: number;
  selectable?: boolean;
  selectionActions?: ISelectionAction[];
  filterLabel?: string;
  exportFileName?: string;
  rowColor?: (row: TData) => string;
  onTableInstance?: (tableInstance: any) => void;
  pagination?: Partial<Pagination>;
  manualPagination?: boolean;
  onPaginationChange?: (pagination: { page: number; limit: number }) => void;
  noWrapHeaders?: boolean;
  noWrapCells?: boolean;
  tableClassName?: string;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  filterBy,
  filterLabel,
  exportTable = false,
  exportTableData = [],
  isLoading = false,
  showControlColumns = true,
  pageSize = 10,
  selectable = false,
  selectionActions,
  exportFileName,
  rowColor,
  onTableInstance,

  manualPagination = false,
  pagination = {
    page: 1,
    perPage: 10,
  },
  onPaginationChange,
  noWrapHeaders = false,
  noWrapCells = false,
  tableClassName = '',
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});

  const exportData = exportTableData.length ? exportTableData : data;

  const paginationFilter = React.useMemo(
    () => ({
      pageIndex: pagination?.page ? pagination.page - 1 : 0,
      pageSize: pagination.perPage || 10,
    }),
    [pagination?.page, pagination?.perPage]
  );

  const setPaginationFilter = React.useCallback(
    (updater: any) => {
      let nextPagination =
        typeof updater === "function" ? updater(paginationFilter) : updater;

      if (onPaginationChange) {
        onPaginationChange({
          page: nextPagination.pageIndex + 1,
          limit: nextPagination.pageSize,
        });
      }
    },
    [onPaginationChange, paginationFilter]
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,

    // Somente ser for paginação manual adiciona essas propriedades
    ...(manualPagination
      ? {
          manualPagination: manualPagination,
          onPaginationChange: setPaginationFilter,
          pageCount: pagination?.lastPage,
        }
      : {}),

    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pagination: paginationFilter,
    },
    initialState: {
      pagination: manualPagination
        ? paginationFilter
        : {
            pageSize: pageSize,
          },
    },
  });

  React.useEffect(() => {
    if (onTableInstance) {
      onTableInstance(table); // Passa a instância da tabela para o componente pai
    }
  }, [table, onTableInstance]);

  if (isLoading) {
    return <Skeleton />;
  }

  if (!data.length) {
    return (
      <div className="flex items-center justify-center h-24 text-center font-sans">
        Sem resultados.
      </div>
    );
  }

  return (
    <div>
      <div className="w-full mb-4 flex flex-col md:flex-row gap-3 items-center justify-between">
        {filterBy && (
          <Input
            placeholder={`Filtrar ${
              filterLabel?.toLowerCase() ?? filterBy.toLowerCase()
            }...`}
            value={
              (table.getColumn(filterBy)?.getFilterValue() as string) ?? ""
            }
            onChange={(event) =>
              table.getColumn(filterBy)?.setFilterValue(event.target.value)
            }
            className="max-w-sm focus-visible:ring-primary-500"
          />
        )}

        {!!selectionActions?.length &&
          selectionActions.map((btn) => (
            <Button.Root
              onClick={() => btn.action(table.getFilteredSelectedRowModel())}
              size="sm"
              disabled={
                !table.getFilteredSelectedRowModel().rows.length ||
                !btn.hasPermission
              }
              variant={btn.variant}
            >
              {btn.label ?? "Action"} (
              {table.getFilteredSelectedRowModel().rows.length}/
              {table.getFilteredRowModel().rows.length})
            </Button.Root>
          ))}

        <div className="flex flex-1 gap-3 w-full justify-end">
          {exportTable && (
            <ExportExcel data={exportData} filename={exportFileName} />
          )}
          {showControlColumns && <DataTableViewOptions table={table} />}
        </div>
      </div>
      <div className="w-full max-w-full rounded-md border bg-white">
        <Table className={tableClassName}>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead
                      key={header.id}
                      className={`text-sm font-GilroyBold ${noWrapHeaders ? 'whitespace-nowrap' : ''}`}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody className="text-xs font-sans">
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  style={{
                    backgroundColor: rowColor
                      ? rowColor(row.original)
                      : "transparent",
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={`font-GilroySemibold uppercase ${noWrapCells ? 'whitespace-nowrap' : ''}`}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center font-sans"
                >
                  Sem resultados.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-start space-x-2 py-4">
        {selectable && (
          <div className="font-GilroyRegular flex-1 text-sm text-muted-foreground">
            {table.getFilteredSelectedRowModel().rows.length}{" "}
            {`De ${table.getFilteredRowModel().rows.length} linha(s) selecionada(s).`}
          </div>
        )}
        <DataTablePagination table={table} />
      </div>
    </div>
  );
}
